import { useTheme } from "../ThemeProvider";
import YesNoField from "./YesNoField";
import { parseEuroAmount, formatEuroAmount } from "../../utils/moneyParse";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";

/** Quién asume el gasto / si va incluido en la renta. */
export const ALQUILER_SERV_OPCIONES = [
  { value: "", label: "—" },
  { value: "incluido", label: "Incluido en renta" },
  { value: "inquilino", label: "Paga inquilino" },
  { value: "propietario", label: "Paga propietario" },
];

export const ALQUILER_SERVICIOS = [
  { name: "alquiler_serv_comunidad", label: "Comunidad" },
  { name: "alquiler_serv_ibi", label: "IBI" },
  { name: "alquiler_serv_agua", label: "Agua" },
  { name: "alquiler_serv_luz", label: "Luz" },
  { name: "alquiler_serv_gas", label: "Gas" },
  { name: "alquiler_serv_basuras", label: "Basuras" },
  { name: "alquiler_serv_internet", label: "Internet" },
  { name: "alquiler_serv_calefaccion", label: "Calefacción" },
];

export const ALQUILER_DEFAULTS = {
  alquiler_serv_comunidad: "propietario",
  alquiler_serv_ibi: "propietario",
  alquiler_serv_agua: "inquilino",
  alquiler_serv_luz: "inquilino",
  alquiler_serv_gas: "inquilino",
  alquiler_serv_basuras: "inquilino",
  alquiler_serv_internet: "inquilino",
  honorarios_alquiler_tipo: "propietario",
};

export function patchAlquilerDefaultsVacios(formLike) {
  if (!formLike || typeof formLike !== "object") return null;
  const patch = {};
  for (const [k, v] of Object.entries(ALQUILER_DEFAULTS)) {
    const cur = formLike[k];
    if (cur == null || cur === "") patch[k] = v;
  }
  return Object.keys(patch).length ? patch : null;
}

const TIPOS_CONTRATO = [
  { value: "", label: "—" },
  { value: "LAU", label: "LAU (vivienda habitual)" },
  { value: "temporal", label: "Temporal" },
  { value: "habitacion", label: "Habitación" },
  { value: "otro", label: "Otro" },
];

const HONORARIOS_TIPO = [
  { value: "", label: "—" },
  { value: "inquilino", label: "Inquilino" },
  { value: "propietario", label: "Propietario" },
  { value: "ambos", label: "Ambos" },
  { value: "ninguno", label: "Ninguno" },
];

function CampoNumero({ label, name, unidad, form, cambiarDirecto, inputStyle, labelStyle, decimales = false, disabled = false, tv = (x) => x }) {
  const raw = form[name];
  let valorInicial = "";
  if (raw != null && raw !== "") {
    if (decimales && Number.isFinite(parseEuroAmount(raw))) {
      valorInicial = formatEuroAmount(raw);
    } else {
      const n = Number(raw);
      valorInicial = Number.isFinite(n) ? String(n) : String(raw);
    }
  }

  return (
    <div style={{ minWidth: 140 }}>
      <label style={labelStyle}>{tv(label)}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="text"
          inputMode={decimales ? "decimal" : "numeric"}
          name={name}
          defaultValue={valorInicial}
          onBlur={(e) => {
            if (disabled) return;
            if (decimales) {
              const n = parseEuroAmount(e.target.value);
              cambiarDirecto(name, Number.isFinite(n) ? n : "");
              e.target.value = Number.isFinite(n) ? formatEuroAmount(n) : "";
            } else {
              const n = parseInt(String(e.target.value).replace(/\./g, ""), 10);
              cambiarDirecto(name, Number.isFinite(n) ? n : "");
              e.target.value = Number.isFinite(n) ? String(n) : "";
            }
          }}
          style={{ ...inputStyle, flex: 1, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
          disabled={disabled}
          title={disabled ? "No tienes permiso para modificar" : undefined}
        />
        {unidad && (
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>{tv(unidad)}</span>
        )}
      </div>
    </div>
  );
}

function SelectCampo({ label, name, options, form, cambiarDirecto, inputStyle, labelStyle, disabled = false, tv = (x) => x }) {
  return (
    <div style={{ minWidth: 160 }}>
      <label style={labelStyle}>{tv(label)}</label>
      <select
        name={name}
        value={form[name] ?? ""}
        onChange={(e) => { if (!disabled) cambiarDirecto(name, e.target.value || null); }}
        disabled={disabled}
        title={disabled ? "No tienes permiso para modificar" : undefined}
        style={{ ...inputStyle, width: "100%", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
      >
        {options.map((o) => (
          <option key={o.value || "_empty"} value={o.value}>{tv(o.label)}</option>
        ))}
      </select>
    </div>
  );
}

function BloqueTitulo({ children, c, tv = (x) => x }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      margin: "4px 0 12px", width: "100%",
    }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: "#0d9488",
        textTransform: "uppercase", letterSpacing: "0.07em",
        background: c.bgCard, padding: "0 8px",
      }}>
        {tv(children)}
      </span>
      <div style={{ flex: 1, height: 1, background: c.sectionBorder || c.chipBorder }} />
    </div>
  );
}

export default function SeccionAlquiler(props) {
  const { form, cambiarDirecto } = props;
  const { tv } = usePropiedadUi(props);
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const puedeVerFianza = puede("alquiler_fianza", "ver");
  const puedeModificarFianza = puede("alquiler_fianza", "modificar");
  const puedeVerAceptaMascotas = puede("acepta_mascotas", "ver");
  const puedeModificarAceptaMascotas = puede("acepta_mascotas", "modificar");
  const puedeVerAvalRequerido = puede("aval_requerido", "ver");
  const puedeModificarAvalRequerido = puede("aval_requerido", "modificar");
  const puedeVerGarantiaAdicional = puede("garantia_adicional_euros", "ver");
  const puedeModificarGarantiaAdicional = puede("garantia_adicional_euros", "modificar");
  const puedeVerDuracionContrato = puede("duracion_contrato_meses", "ver");
  const puedeModificarDuracionContrato = puede("duracion_contrato_meses", "modificar");
  const puedeVerTipoContrato = puede("tipo_contrato", "ver");
  const puedeModificarTipoContrato = puede("tipo_contrato", "modificar");
  const puedeVerServicios = puede("alquiler_servicios", "ver");
  const puedeModificarServicios = puede("alquiler_servicios", "modificar");
  const puedeVerSeguro = puede("seguro_alquiler_contratado", "ver");
  const puedeModificarSeguro = puede("seguro_alquiler_contratado", "modificar");
  const puedeVerHonorarios = puede("honorarios_alquiler", "ver");
  const puedeModificarHonorarios = puede("honorarios_alquiler", "modificar");
  const puedeVerObservaciones = puede("alquiler_observaciones", "ver");
  const puedeModificarObservaciones = puede("alquiler_observaciones", "modificar");

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 6, display: "block",
  };
  const inputStyle = {
    padding: "8px 12px", border: `1px solid ${c.chipBorder}`,
    borderRadius: 8, fontSize: 13, color: c.textPrimary,
    background: isDark ? "#0a0e16" : "#dceeff", outline: "none",
    boxShadow: isDark
      ? "inset 0 3px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3)"
      : "inset 0 3px 6px rgba(100,140,200,0.18), inset 0 1px 3px rgba(80,120,180,0.13)",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <BloqueTitulo c={c} tv={tv}>Fianza y condiciones</BloqueTitulo>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start", marginBottom: 8 }}>
        {puedeVerFianza && <CampoNumero label="Fianza" name="fianza_meses" unidad="meses" form={form} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} disabled={!puedeModificarFianza} tv={tv} />}
        {puedeVerFianza && <CampoNumero label="Fianza" name="fianza_euros" unidad="€" form={form} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} decimales disabled={!puedeModificarFianza} tv={tv} />}
        {puedeVerAceptaMascotas && <YesNoField name="acepta_mascotas" label="Acepta mascotas" form={form} cambiarDirecto={cambiarDirecto} labelAbove disabled={!puedeModificarAceptaMascotas} />}
        {puedeVerAvalRequerido && <YesNoField name="aval_requerido" label="Aval requerido" form={form} cambiarDirecto={cambiarDirecto} labelAbove disabled={!puedeModificarAvalRequerido} />}
        {puedeVerGarantiaAdicional && <CampoNumero label="Garantía adicional" name="garantia_adicional_euros" unidad="€" form={form} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} decimales disabled={!puedeModificarGarantiaAdicional} tv={tv} />}
        {puedeVerDuracionContrato && <CampoNumero label="Duración contrato" name="duracion_contrato_meses" unidad="meses" form={form} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} disabled={!puedeModificarDuracionContrato} tv={tv} />}
        {puedeVerTipoContrato && <SelectCampo label="Tipo de contrato" name="tipo_contrato" options={TIPOS_CONTRATO} form={form} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} disabled={!puedeModificarTipoContrato} tv={tv} />}
      </div>

      {puedeVerServicios && (
      <>
      <BloqueTitulo c={c} tv={tv}>Servicios y suministros (quién paga / incluido)</BloqueTitulo>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: c.textMuted, lineHeight: 1.45, maxWidth: 720 }}>
        <strong>{tv("Incluido en renta")}</strong>
        {`: ${tv("ese gasto ya va dentro del precio de alquiler")}. `}
        <strong>{tv("Paga propietario")}</strong>
        {`: ${tv("lo asume el propietario aparte (no está metido en la renta anunciada)")}. `}
        <strong>{tv("Paga inquilino")}</strong>
        {`: ${tv("el arrendatario paga esa factura")}.`}
      </p>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        gap: 12,
        marginBottom: 8,
      }}>
        {ALQUILER_SERVICIOS.map((s) => (
          <SelectCampo
            key={s.name}
            label={s.label}
            name={s.name}
            options={ALQUILER_SERV_OPCIONES}
            form={form}
            cambiarDirecto={cambiarDirecto}
            inputStyle={inputStyle}
            labelStyle={labelStyle}
            disabled={!puedeModificarServicios}
            tv={tv}
          />
        ))}
      </div>
      </>
      )}

      {puedeVerSeguro && (
      <>
      <BloqueTitulo c={c} tv={tv}>Seguro de alquiler</BloqueTitulo>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12, maxWidth: 560 }}>
        <YesNoField
          name="seguro_alquiler_contratado"
          label="Propietario tiene contratado seguro de alquiler"
          form={form}
          cambiarDirecto={cambiarDirecto}
          labelAbove
          disabled={!puedeModificarSeguro}
        />
        <p style={{ margin: 0, fontSize: 12, color: c.textMuted, lineHeight: 1.45 }}>
          {tv("Si está contratado, la aprobación del arrendatario puede depender de la agencia aseguradora (no solo de la inmobiliaria).")}
        </p>
      </div>
      </>
      )}

      {puedeVerHonorarios && (
      <>
      <BloqueTitulo c={c} tv={tv}>Honorarios de alquiler</BloqueTitulo>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "stretch",
      }}>
        <SelectCampo
          label="Quién paga honorarios"
          name="honorarios_alquiler_tipo"
          options={HONORARIOS_TIPO}
          form={form}
          cambiarDirecto={cambiarDirecto}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
          disabled={!puedeModificarHonorarios}
          tv={tv}
        />
        <CampoNumero
          label="Importe honorarios"
          name="honorarios_alquiler_importe"
          unidad="€"
          form={form}
          cambiarDirecto={cambiarDirecto}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
          decimales
          disabled={!puedeModificarHonorarios}
          tv={tv}
        />
        {puedeVerObservaciones && (
        <div style={{ flex: "1 1 240px", minWidth: 200, display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>{tv("Observaciones")}</label>
          <textarea
            name="alquiler_observaciones"
            value={form.alquiler_observaciones ?? ""}
            onChange={(e) => cambiarDirecto("alquiler_observaciones", e.target.value)}
            placeholder={tv("Notas de alquiler (fianza, contrato, servicios…)")}
            rows={3}
            disabled={!puedeModificarObservaciones}
            title={!puedeModificarObservaciones ? "No tienes permiso para modificar" : undefined}
            style={{
              ...inputStyle,
              width: "100%",
              flex: 1,
              minHeight: 72,
              resize: "vertical",
              fontFamily: "inherit",
              lineHeight: 1.45,
              boxSizing: "border-box",
              opacity: puedeModificarObservaciones ? 1 : 0.6,
              cursor: puedeModificarObservaciones ? "text" : "not-allowed",
            }}
          />
        </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
